/* eslint-disable arrow-body-style */
import React, { useMemo, useState, useEffect } from 'react';
import classnames from 'classnames';
import { useDebounceFn } from 'ahooks';
import { Segment, Table, TableColumn } from 'tea-component';
import CardSlot from '@gp/components/CardSlot';
import TrendsCard from '../TrendsCard';
import { reportBtnOperationLog } from '@gp/hooks/usePageLog';
import useGetDateRange, { useGetFetchBudgetTrendingDownload, useGetFetchHeadcountTrendDownload } from '../../hooks';
import { getDevCostTrends, getHeadcountTrends } from '../../services';
import useLineCharts from '@intelligence/components/Chart/hooks/useLineCharts';
import { generateLineData } from '@intelligence/pages/TopCharts/Companies/components/CompaniesCore/components/Visualis/utils';
import {
  stream2Object,
  downloadFileByBlob,
  getDownLoadCSVResponseHeaderFileName,
  getRoundIntegerNum,
} from '@gp/utils/common';
import {
  getDateRangeByCategory,
  getLineXAxisData,
  mergeLineOptions,
  getTableDynamicColumns,
  typeNameFormatter,
} from '../../utils';
import {
  getLineOrPieData,
  lineChartFormatting,
  lineFormatterTooltip,
  transformDataLineFlatMap,
} from '../../utils/echartsUtils';
import { formatDataTableBudgetColumnKey, transformDataTableHeadcountColumnKey } from '../../utils/dataTransfer';
import { FILE_TYPES_MAP } from '@gp/constants/const';
import {
  Y_AXIS_NAME_CURRENCY_OPTION,
  Y_AXIS_NAME_CURRENCY_GRID_OPTION,
  BASE_ECHART_OPTION,
} from '@gp/constants/echarts';
import { FinancialsType, FinancialsDataMode } from '../../enums';

import type { IBaseProps } from '@home/types/common';
import type { SegmentOption } from 'tea-component/lib/segment/SegmentOption';
import type { IProjectDetailsFinanceTrendsParamsDTO } from '@gp/types/projectDetail';

interface IFinanceTableAndEchartsProps extends IBaseProps {
  projectId?: string;
  studioId?: string;
}

const CSV_FILE_TYPE = Number.parseInt(FILE_TYPES_MAP.CSV, 10);

const { scrollable } = Table.addons;
const scrollableAddon = scrollable({
  minWidth: 1100,
  maxHeight: 350,
});
const addons = [scrollableAddon];

const INITIAL_CATEGORY = '3';

const CATEGORIES: SegmentOption[] = [
  { text: 'All Months', value: '1' },
  { text: 'Last 6 Months', value: '2' },
  { text: 'Last 12 Months', value: '3' },
  { text: 'By Milestone', value: '4' },
];

const DEV_COST_TITLE = 'Development Cost';

const DEV_COST_TITLE_TIPS_CFG = {
  definition:
    'Research & Development Staff Cost + Outsourcing + Other Research & Development Expenses - Video Games Tax Relief (If Any)',
  source: 'Rolling Forecast',
  updateCadence: 'Quarterly',
};

const DEV_HEADCOUNT_TITLE = 'Development Headcount';

const DEV_HEADCOUNT_TITLE_TIPS_CFG = {
  definition: 'Research & Development headcount and full-time contractor directly related to the project.',
  source: 'Rolling Forecast',
  updateCadence: 'Quarterly',
};

const TABLE_TYPE_COLUMN = {
  key: 'type',
  header: 'Type' as TableColumn['header'],
  width: 140,
  fixed: 'left' as TableColumn['fixed'],
  render: (cvm: { type: string; [key: string]: any }) => typeNameFormatter(cvm?.type),
};

const getFinancialsDataMode = (payload: string): FinancialsDataMode =>
  payload === '4' ? FinancialsDataMode.ByMilestone : FinancialsDataMode.ByMonth;

const FinanceTableAndEcharts: React.FC<IFinanceTableAndEchartsProps> = (props) => {
  const { style, className, projectId, studioId } = props;
  // 页签标识
  const [category, setCategory] = useState(INITIAL_CATEGORY);
  // 是否为byMilestone模式
  const financialsDataMode = getFinancialsDataMode(category);
  // 获取页签下对应的开始结束时间
  const { start, end } = useGetDateRange(category);
  // 入参缓存
  const params = useMemo<{
    project_id: string;
    studio_id: string;
    start_date: string;
    end_date: string;
  }>(() => {
    return {
      project_id: projectId || '',
      studio_id: studioId || '',
      start_date: start || '',
      end_date: end || '',
    };
  }, [projectId, studioId, start, end]);

  const devCostTrendsTitle = `${DEV_COST_TITLE} by ${
    financialsDataMode === FinancialsDataMode.ByMilestone ? 'Milestone' : 'Month'
  }`;

  //  Trends下载数据
  const { runAsync: runTrend } = useGetFetchBudgetTrendingDownload(financialsDataMode);
  //  Headcount下载数据
  const { runAsync: runHeadCount } = useGetFetchHeadcountTrendDownload(financialsDataMode);

  const [budgetTrendingDownloading, setBudgetTrendingDownloading] = useState(false);
  //   Trends 载方法
  const { run: handleDownLoadBudgetTrending } = useDebounceFn(
    () => {
      setBudgetTrendingDownloading(true);
      runTrend({
        ...params,
        file_type: CSV_FILE_TYPE,
      })
        .then((res: any) => {
          const fileName = getDownLoadCSVResponseHeaderFileName(res?.headers);
          downloadFileByBlob(res?.data, fileName);
          setBudgetTrendingDownloading(false);
        })
        .catch(async (e) => {
          setBudgetTrendingDownloading(false);
          const errRes = await stream2Object(e);
          console.error(errRes);
        });
      reportBtnOperationLog('DETAIL_FINANCE_DEV_COST_BY_MONTH_CSV_DOWNLOAD_SUBMIT');
    },
    { wait: 300 },
  );

  const [headcountDownloading, setHeadcountDownloading] = useState(false);
  //   Headcount下载方法
  const { run: handleDownLoadHeadcountTrending } = useDebounceFn(
    () => {
      setHeadcountDownloading(true);
      runHeadCount({
        ...params,
        file_type: CSV_FILE_TYPE,
      })
        .then((res: any) => {
          // 拿到响应头中的文件名称
          const fileName = getDownLoadCSVResponseHeaderFileName(res?.headers);
          downloadFileByBlob(res?.data, fileName);
          setHeadcountDownloading(false);
        })
        .catch(async (e) => {
          setHeadcountDownloading(false);
          const errRes = await stream2Object(e);
          console.error(errRes);
        });
      reportBtnOperationLog('DETAIL_FINANCE_HEADCOUNT_CSV_DOWNLOAD_SUBMIT');
    },
    { wait: 300 },
  );

  const [isDevCostTrendsLoading, setIsDevCostTrendsLoading] = useState(true);
  const [isDevCostTrendsError, setIsDevCostTrendsError] = useState(false);
  const [devCostTrendsData, setDevCostTrendsData] = useState({ list: [] });
  // @ts-ignore
  const devCostTrendsColumns = useMemo<TableColumn<any>[]>(() => {
    const dynamicColumns = getTableDynamicColumns(financialsDataMode, devCostTrendsData?.list, {
      widthByMilestone: 160,
      widthByMonth: 120,
    });
    return [TABLE_TYPE_COLUMN, ...(dynamicColumns || [])];
  }, [financialsDataMode, devCostTrendsData]);
  const devCostTrendsRecords = useMemo(
    () => formatDataTableBudgetColumnKey(devCostTrendsData?.list),
    [devCostTrendsData],
  );
  // Trends、Headcount柱状图数据
  const devCostTrendsBarOption = useMemo(() => {
    return lineChartFormatting?.(
      devCostTrendsData?.list,
      null,
      DEV_COST_TITLE,
      FinancialsType.DevCost,
      financialsDataMode,
    );
  }, [financialsDataMode, devCostTrendsData]);
  const devCostTrendsLineData = {
    source: generateLineData(
      'top_companies_revenue',
      getLineOrPieData(transformDataLineFlatMap(devCostTrendsData?.list), financialsDataMode),
    ),
  };
  // Trends折线图数据
  const { lineOption: devCostTrendsLineOption } = useLineCharts({
    topChartsType: 'top_companies_revenue',
    imageType: 'mobile',
    metric: 'source',
    gridConfig: {
      ...BASE_ECHART_OPTION.grid,
      ...Y_AXIS_NAME_CURRENCY_GRID_OPTION,
    },
    yAxisConfig: {
      ...Y_AXIS_NAME_CURRENCY_OPTION,
    },
    lineData: devCostTrendsLineData,
    lineXAxisData: getLineXAxisData(devCostTrendsLineData, 'source', financialsDataMode),
    tooltipFormatter: (params: any) => lineFormatterTooltip(params, DEV_COST_TITLE),
  });

  const updateDevCostData = async (
    params: IProjectDetailsFinanceTrendsParamsDTO & {
      mode: FinancialsDataMode;
    },
  ) => {
    try {
      setIsDevCostTrendsLoading(true);
      const res = await getDevCostTrends(params);
      setDevCostTrendsData(res as any);
      setIsDevCostTrendsError(false);
    } catch (exception) {
      setIsDevCostTrendsError(true);
    } finally {
      setIsDevCostTrendsLoading(false);
    }
  };

  const [isHeadcountTrendsLoading, setIsHeadcountTrendsLoading] = useState(true);
  const [isHeadcountTrendsError, setIsHeadcountTrendsError] = useState(false);
  const [headcountTrendsData, setHeadcountTrendsData] = useState({ list: [], table: [] });
  // @ts-ignore
  const headcountTrendsColumns = useMemo<TableColumn<any>[]>(() => {
    const dynamicColumns = getTableDynamicColumns(financialsDataMode, headcountTrendsData?.list, {
      widthByMilestone: 160,
      widthByMonth: 120,
    });
    return [TABLE_TYPE_COLUMN, ...(dynamicColumns || [])];
  }, [financialsDataMode, headcountTrendsData]);
  const headcountTrendsRecords = useMemo(
    () => transformDataTableHeadcountColumnKey(headcountTrendsData?.table),
    [headcountTrendsData],
  );
  // Trends、Headcount柱状图数据
  const headcountTrendsBarOption = useMemo(() => {
    return lineChartFormatting?.(
      headcountTrendsData?.list,
      'headcount_trend',
      DEV_HEADCOUNT_TITLE,
      FinancialsType.DevHeadcount,
      financialsDataMode,
    );
  }, [financialsDataMode, headcountTrendsData]);
  const headcountTrendsLineData = {
    source: generateLineData(
      'top_companies_revenue',
      getLineOrPieData(transformDataLineFlatMap(headcountTrendsData?.list), financialsDataMode),
    ),
  };
  // Headcount折线图数据
  const { lineOption: headcountTrendsLineOption } = useLineCharts({
    topChartsType: 'top_companies_revenue',
    imageType: 'mobile',
    metric: 'source',
    lineData: headcountTrendsLineData,
    lineXAxisData: getLineXAxisData(headcountTrendsLineData, 'source', financialsDataMode),
    tooltipFormatter: (params: any) => lineFormatterTooltip(params, DEV_HEADCOUNT_TITLE, 'headcount_trend'),
    yAxisFormatter: getRoundIntegerNum,
  });

  const updateHeadcountData = async (
    params: IProjectDetailsFinanceTrendsParamsDTO & {
      mode: FinancialsDataMode;
    },
  ) => {
    try {
      setIsHeadcountTrendsLoading(true);
      const res = await getHeadcountTrends(params);
      setHeadcountTrendsData(res as any);
      setIsHeadcountTrendsError(false);
    } catch (exception) {
      setIsHeadcountTrendsError(true);
    } finally {
      setIsHeadcountTrendsLoading(false);
    }
  };

  const handleCategoryChange = (payload: string) => {
    setCategory(payload);
    const { start, end } = getDateRangeByCategory(payload);
    const params = {
      mode: getFinancialsDataMode(payload),
      project_id: projectId || '',
      studio_id: studioId || '',
      start_date: start || '',
      end_date: end || '',
    };
    updateDevCostData(params);
    updateHeadcountData(params);
  };

  useEffect(() => {
    if (!projectId || !studioId) return;
    const { start, end } = getDateRangeByCategory(INITIAL_CATEGORY);
    const params = {
      mode: FinancialsDataMode.ByMonth,
      project_id: projectId || '',
      studio_id: studioId || '',
      start_date: start || '',
      end_date: end || '',
    };
    updateDevCostData(params);
    updateHeadcountData(params);
  }, [projectId, studioId]);

  return (
    <CardSlot style={style} className={classnames('dbg-finance-detail__trends-module', className)} title="Trends">
      <Segment
        className="dbg-finance-detail-segement"
        options={CATEGORIES}
        value={category}
        onChange={handleCategoryChange}
      />
      <div className="dbg-finance-detail-context">
        <TrendsCard
          isLoading={isDevCostTrendsLoading}
          isError={isDevCostTrendsError}
          isNoData={!Array.isArray(devCostTrendsData?.list) || !devCostTrendsData.list.length}
          title={devCostTrendsTitle}
          titleTipsCfg={DEV_COST_TITLE_TIPS_CFG}
          addons={addons}
          columns={devCostTrendsColumns}
          records={devCostTrendsRecords}
          barOption={devCostTrendsBarOption}
          lineOption={mergeLineOptions(devCostTrendsLineOption)}
          downloadLoading={budgetTrendingDownloading}
          onDownload={handleDownLoadBudgetTrending}
        />
        <TrendsCard
          isLoading={isHeadcountTrendsLoading}
          isError={isHeadcountTrendsError}
          isNoData={!Array.isArray(headcountTrendsData?.list) || !headcountTrendsData.list.length}
          title={DEV_HEADCOUNT_TITLE}
          titleTipsCfg={DEV_HEADCOUNT_TITLE_TIPS_CFG}
          addons={addons}
          columns={headcountTrendsColumns}
          records={headcountTrendsRecords}
          barOption={headcountTrendsBarOption}
          lineOption={mergeLineOptions(headcountTrendsLineOption)}
          downloadLoading={headcountDownloading}
          onDownload={handleDownLoadHeadcountTrending}
        />
      </div>
    </CardSlot>
  );
};

export default React.memo(FinanceTableAndEcharts);
